import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  Activity,
  BadgeCheck,
  Building2,
  ClipboardList,
  CreditCard,
  Download,
  Gauge,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Server,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import { api, downloadRecoveryBundle, patch, post, put, remove, setCsrf, type AdminUser } from './api.js';
import {
  attentionMessage,
  canEditSubscriptionPbxLimit,
  displayPlanCode,
  replacementPlanChoices,
  subscriptionPbxLimits,
  productPlanChoices,
  type PlanChoice,
} from './admin-model.js';
import {
  coreComparisonFeatures,
  displayEntitlement,
  filterFeatures,
  matrixChanges,
  planCapacityLabel,
  protectedCorePabxState,
  type EntitlementValue,
  type FeatureRecord,
  type PlanRecord,
} from './catalog-model.js';
import { downloadCsv, downloadPdf, type ExportColumn } from './export.js';
import { initialLoginStage, type LoginStage } from './login-flow.js';

type Page =
  | 'dashboard'
  | 'customers'
  | 'subscriptions'
  | 'licenses'
  | 'instances'
  | 'catalog'
  | 'audit'
  | 'administration';
type Json = Record<string, unknown>;
const nav: Array<{ page: Page; label: string; icon: typeof Gauge }> = [
  { page: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { page: 'customers', label: 'Customers', icon: Building2 },
  { page: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
  { page: 'licenses', label: 'Licenses', icon: KeyRound },
  { page: 'instances', label: 'Instances', icon: Server },
  { page: 'catalog', label: 'Plans & Features', icon: ClipboardList },
  { page: 'audit', label: 'Audit Log', icon: Activity },
  { page: 'administration', label: 'Administration', icon: Settings },
];

export function App() {
  const [me, setMe] = useState<AdminUser | null | undefined>(undefined);
  const [page, setPage] = useState<Page>('dashboard');
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    void api<AdminUser>('/api/admin/v1/auth/me')
      .then(setMe)
      .catch(() => setMe(null));
  }, []);
  if (me === undefined)
    return (
      <div className="center">
        <div className="spinner" />
        <span>Loading Secure Portal…</span>
      </div>
    );
  if (!me || !me.authenticated || me.must_change_password)
    return <LoginFlow user={me} onAuthenticated={setMe} />;
  return (
    <div className="app-shell">
      <aside className={menu ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <div className="brand-mark">CDR</div>
          <div>
            <strong>License Manager</strong>
            <small>CDRMetrics</small>
          </div>
        </div>
        <nav>
          {nav.map((item) => (
            <button
              className={page === item.page ? 'active' : ''}
              key={item.page}
              onClick={() => {
                setPage(item.page);
                setMenu(false);
              }}
            >
              <item.icon size={18} />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <ShieldCheck size={16} />
          <span>Security Controls Active</span>
        </div>
      </aside>
      <main>
        <header>
          <button
            className="icon mobile"
            aria-label="Open navigation"
            onClick={() => setMenu(true)}
          >
            <Menu />
          </button>
          <GlobalSearch onNavigate={setPage} />
          <span className="env">{import.meta.env.MODE.toUpperCase()}</span>
          <div className="user">
            <span>{me.display_name}</span>
            <small>{me.role}</small>
          </div>
          <button
            className="icon"
            aria-label="Log out"
            onClick={() => void post('/api/admin/v1/auth/logout').finally(() => setMe(null))}
          >
            <LogOut size={18} />
          </button>
        </header>
        <section className="content">
          <PageContent page={page} role={me.role} />
        </section>
      </main>
    </div>
  );
}

function GlobalSearch({ onNavigate }: { onNavigate: (page: Page) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Json[]>([]);
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const timer = setTimeout(() => {
      void api<Json[]>(`/api/admin/v1/search?q=${encodeURIComponent(query.trim())}`)
        .then(setResults)
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  return (
    <div className="search-wrap">
      <div className="global-search">
        <Search size={17} />
        <input
          aria-label="Global search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search Customers, Keys, Instances…"
        />
      </div>
      {results.length > 0 && (
        <div className="search-results">
          {results.map((row, index) => (
            <button
              key={rowKey(row, index)}
              onClick={() => {
                const type = row.type;
                onNavigate(
                  type === 'CUSTOMER' ? 'customers' : type === 'LICENSE' ? 'licenses' : 'instances',
                );
                setQuery('');
                setResults([]);
              }}
            >
              <span>{value(row.label)}</span>
              <small>{value(row.type)}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginFlow({
  user,
  onAuthenticated,
}: {
  user: AdminUser | null;
  onAuthenticated: (user: AdminUser) => void;
}) {
  const [stage, setStage] = useState<LoginStage>(initialLoginStage(user));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [token, setToken] = useState('');
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (stage !== 'SETUP' || setup) return;
    setBusy(true);
    void post<{ secret: string; otpauth_uri: string }>('/api/admin/v1/auth/mfa/setup')
      .then(setSetup)
      .catch((value: unknown) => {
        setError(value instanceof Error ? value.message : 'MFA enrollment failed');
      })
      .finally(() => setBusy(false));
  }, [setup, stage]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (stage === 'LOGIN') {
        const result = await post<{
          csrf_token: string;
          requires_password_change: boolean;
          requires_mfa_enrollment: boolean;
        }>('/api/admin/v1/auth/login', { email, password });
        setCsrf(result.csrf_token);
        if (result.requires_password_change) setStage('PASSWORD');
        else if (result.requires_mfa_enrollment) {
          const enrollment = await post<{ secret: string; otpauth_uri: string }>(
            '/api/admin/v1/auth/mfa/setup',
          );
          setSetup(enrollment);
          setStage('SETUP');
        } else setStage('MFA');
      } else if (stage === 'PASSWORD') {
        await post('/api/admin/v1/auth/password/change', {
          current_password: current || password,
          new_password: next,
        });
        const enrollment = await post<{ secret: string; otpauth_uri: string }>(
          '/api/admin/v1/auth/mfa/setup',
        );
        setSetup(enrollment);
        setStage('SETUP');
      } else {
        const result = await post<{ authenticated: boolean; recovery_codes?: string[] }>(
          '/api/admin/v1/auth/mfa/verify',
          { token },
        );
        if (result.recovery_codes)
          alert(
            `Store these recovery codes securely. They are shown once:\n\n${result.recovery_codes.join('\n')}`,
          );
        onAuthenticated(await api<AdminUser>('/api/admin/v1/auth/me'));
      }
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-page">
      <div className="login-panel">
        <div className="login-brand">
          <div className="brand-mark large">CDR</div>
          <p>Secure licensing operations</p>
        </div>
        <form onSubmit={submit}>
          <div className="eyebrow">CDRMETRICS</div>
          <h1>
            {stage === 'LOGIN'
              ? 'Welcome Back'
              : stage === 'PASSWORD'
                ? 'Secure Your Account'
                : stage === 'SETUP'
                  ? 'Enroll An Authenticator'
                  : 'Verify Your Identity'}
          </h1>
          <p className="muted">
            {stage === 'LOGIN'
              ? 'Sign in to the License Manager administration portal.'
              : stage === 'SETUP'
                ? 'Add this secret to your authenticator, then enter the current six-digit code.'
                : 'Multi-factor authentication is mandatory for portal access.'}
          </p>
          {stage === 'LOGIN' && (
            <>
              <Field label="Email" type="email" value={email} set={setEmail} />
              <Field label="Password" type="password" value={password} set={setPassword} />
            </>
          )}
          {stage === 'PASSWORD' && (
            <>
              <Field label="Current password" type="password" value={current} set={setCurrent} />
              <Field
                label="New password (14+ characters)"
                type="password"
                value={next}
                set={setNext}
              />
            </>
          )}
          {stage === 'SETUP' && (
            <>
              <div className="secret-box">
                <span>Authenticator Secret</span>
                <code>{setup?.secret}</code>
              </div>
              <Field label="Verification code" inputMode="numeric" value={token} set={setToken} />
            </>
          )}
          {stage === 'MFA' && (
            <Field label="Authenticator or recovery code" value={token} set={setToken} />
          )}{' '}
          {error && <div className="error">{error}</div>}
          <button className="primary full" disabled={busy}>
            {busy
              ? 'Please wait…'
              : stage === 'LOGIN'
                ? 'Sign in'
                : stage === 'PASSWORD'
                  ? 'Change password'
                  : stage === 'SETUP'
                    ? 'Verify & finish'
                    : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  set,
  ...props
}: {
  label: string;
  value: string;
  set: (v: string) => void;
  type?: string;
  inputMode?: 'numeric';
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} value={value} onChange={(event) => set(event.target.value)} required />
    </label>
  );
}

function PageContent({ page, role }: { page: Page; role: AdminUser['role'] }) {
  if (page === 'dashboard') return <Dashboard />;
  if (page === 'customers') return <Customers readOnly={role === 'READ_ONLY'} />;
  if (page === 'subscriptions') return <Subscriptions readOnly={role === 'READ_ONLY'} />;
  if (page === 'licenses') return <Licenses readOnly={role === 'READ_ONLY'} />;
  if (page === 'instances') return <Instances readOnly={role === 'READ_ONLY'} />;
  if (page === 'catalog') return <Catalog readOnly={role === 'READ_ONLY'} />;
  if (page === 'audit')
    return (
      <ResourceTable
        title="Audit Log"
        subtitle="Append-Only Record of Security and Licensing Actions"
        endpoint="/api/admin/v1/audit-events"
        columns={['created_at', 'administrator', 'event_type', 'entity_type', 'request_id']}
      />
    );
  return <Administration role={role} />;
}

function useData<T>(url: string) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState('');
  const reload = useCallback(() => {
    setError('');
    void api<T>(url)
      .then(setData)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Failed to load'),
      );
  }, [url]);
  useEffect(reload, [reload]);
  return { data, error, reload };
}
function useDebouncedValue<T>(input: T, delay = 250): T {
  const [value, setValue] = useState(input);
  useEffect(() => {
    const timer = setTimeout(() => setValue(input), delay);
    return () => clearTimeout(timer);
  }, [delay, input]);
  return value;
}
function listEndpoint(endpoint: string, search: string, filters: Record<string, string>): string {
  const params = new URLSearchParams({ limit: '1000' });
  if (search.trim()) params.set('search', search.trim());
  Object.entries(filters).forEach(([key, filterValue]) => {
    if (filterValue) params.set(key, filterValue);
  });
  return `${endpoint}?${params.toString()}`;
}
function titleCaseLabel(input: string): string {
  const acronyms = new Set(['id', 'ip', 'mfa', 'utc', 'vps', 'json', 'cdrmetrics']);
  return input
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w+/g, (word) =>
      acronyms.has(word) ? word.toUpperCase() : word[0]!.toUpperCase() + word.slice(1),
    );
}
function ListToolbar({
  query,
  setQuery,
  placeholder,
  children,
  title,
  columns,
  rows,
}: {
  query: string;
  setQuery: (value: string) => void;
  placeholder: string;
  children?: ReactNode;
  title: string;
  columns: ExportColumn<Json>[];
  rows: Json[];
}) {
  return (
    <div className="list-toolbar">
      <label className="list-search">
        <Search size={16} />
        <input
          aria-label={`Search ${title}`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
        />
      </label>
      <div className="list-filters">{children}</div>
      <span className="record-count">{rows.length} Records</span>
      <button className="secondary export-button" onClick={() => downloadCsv(title, columns, rows)}>
        <Download size={15} /> CSV
      </button>
      <button className="secondary export-button" onClick={() => downloadPdf(title, columns, rows)}>
        <Download size={15} /> PDF
      </button>
    </div>
  );
}
function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div>
        <div className="eyebrow">OPERATIONS</div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {action}
    </div>
  );
}
function Status({ value }: { value: unknown }) {
  const status = typeof value === 'string' ? value : 'UNKNOWN';
  return <span className={`status s-${status.toLowerCase()}`}>{titleCaseLabel(status)}</span>;
}
function value(value: unknown) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && /^\d{4}-\d\d-\d\dT/.test(value))
    return new Date(value).toLocaleString();
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint')
    return value.toString();
  if (typeof value === 'object') return JSON.stringify(value);
  return '—';
}
function rowKey(row: Json, index: number) {
  const id = row.id;
  return typeof id === 'string' || typeof id === 'number' ? String(id) : String(index);
}
function confirmedReason(reasonPrompt: string, expected: string): string | undefined {
  const reasonText = prompt(reasonPrompt);
  if (!reasonText) return undefined;
  return prompt(`Type ${expected} to confirm this action.`) === expected ? reasonText : undefined;
}
function confirmedDeletion(subject: string): string | undefined {
  return confirmedReason(`Reason for permanently deleting ${subject}`, 'DELETE');
}

function Dashboard() {
  const { data, error } = useData<{
    subscriptions: Array<{ status: string; billing_cycle: string; count: number }>;
    active_instances: number;
    instances_requiring_attention: Json[];
    recent_activity: Json[];
  }>('/api/admin/v1/dashboard');
  const total = (status: string) =>
    data?.subscriptions.filter((x) => x.status === status).reduce((n, x) => n + x.count, 0) ?? 0;
  return (
    <>
      <PageHeader
        title="License Operations"
        subtitle="Current Subscription Health and Installation Connectivity"
      />
      <div className="metrics">
        {[
          ['Active Subscriptions', total('ACTIVE')],
          ['Past Due', total('PAST_DUE')],
          ['Grace', total('GRACE')],
          ['Suspended', total('SUSPENDED')],
          ['Active Instances', data?.active_instances ?? 0],
        ].map(([label, n]) => (
          <div className="metric" key={String(label)}>
            <span>{label}</span>
            <strong>{n}</strong>
          </div>
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      <div className="grid-two">
        <Panel title="Instances Requiring Attention">
          <MiniRows
            rows={data?.instances_requiring_attention ?? []}
            empty="All active instances checked in recently."
            detail={(row) =>
              `${attentionMessage(row.attention_reason, value(row.last_seen_at))} · ${value(row.customer_name)}`
            }
          />
        </Panel>
        <Panel title="Recent License Activity">
          <MiniRows
            rows={data?.recent_activity ?? []}
            empty="No recent license activity."
            title={(row) =>
              typeof row.event_type === 'string'
                ? titleCaseLabel(row.event_type)
                : 'License Activity'
            }
            detail={(row) => `${value(row.customer_name)} · ${value(row.created_at)}`}
          />
        </Panel>
      </div>
    </>
  );
}
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="panel">
      <div className="panel-title">
        <h2>{title}</h2>
      </div>
      {children}
    </div>
  );
}
function MiniRows({
  rows,
  empty,
  title,
  detail,
}: {
  rows: Json[];
  empty: string;
  title?: (row: Json) => ReactNode;
  detail?: (row: Json) => ReactNode;
}) {
  if (!rows.length)
    return (
      <div className="empty">
        <BadgeCheck />
        <p>{empty}</p>
      </div>
    );
  return (
    <div className="mini-rows">
      {rows.map((row, i) => (
        <div key={rowKey(row, i)}>
          <strong>
            {title
              ? title(row)
              : value(
                  row.display_name ??
                    row.hostname ??
                    row.installation_name ??
                    row.event_type ??
                    row.customer_name,
                )}
          </strong>
          <span>
            {detail ? detail(row) : value(row.last_seen_at ?? row.created_at ?? row.app_version)}
          </span>
        </div>
      ))}
    </div>
  );
}

function ResourceTable({
  title,
  subtitle,
  endpoint,
  columns,
  action,
}: {
  title: string;
  subtitle: string;
  endpoint: string;
  columns: string[];
  action?: ReactNode;
}) {
  const { data, error, reload } = useData<Json[]>(endpoint);
  return (
    <>
      <PageHeader title={title} subtitle={subtitle} action={action} />
      {error && <div className="error">{error}</div>}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c}>{titleCaseLabel(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((row, i) => (
              <tr key={rowKey(row, i)}>
                {columns.map((c) => (
                  <td key={c}>
                    {c === 'status' || c.endsWith('_status') ? (
                      <Status value={row[c]} />
                    ) : (
                      value(row[c])
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && (
          <div className="empty">
            <ClipboardList />
            <p>No records yet.</p>
          </div>
        )}
        <button className="text-button" onClick={reload}>
          Refresh
        </button>
      </div>
    </>
  );
}

function PlanCatalog({ readOnly }: { readOnly: boolean }) {
  const {
    data: plans,
    error: planError,
    reload: reloadPlans,
  } = useData<PlanRecord[]>('/api/admin/v1/plans');
  const { data: features, error: featureError } =
    useData<FeatureRecord[]>('/api/admin/v1/features');
  const [draft, setDraft] = useState<Record<string, Record<string, EntitlementValue>>>({});
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<'CDRMETRICS' | 'corepabx'>('CDRMETRICS');
  const resetDraft = useCallback(() => {
    if (!plans) return;
    setDraft(Object.fromEntries(plans.map((plan) => [plan.code, { ...plan.entitlements }])));
    setSaveError('');
  }, [plans]);
  useEffect(resetDraft, [resetDraft]);
  const activePlans = (plans ?? []).filter(
    (plan) => plan.active && plan.product_code === selectedProduct,
  );
  const activeFeatures = (features ?? []).filter(
    (feature) => feature.active && feature.product_code === selectedProduct,
  );
  const isCorePabx = selectedProduct === 'corepabx';
  const protectedCore = features
    ? ['security', 'network_access', 'licensing', 'software_updates', 'multi_tenant'].map(
        (code) => {
          const feature = features.find(
            (entry) => entry.product_code === 'corepabx' && entry.code === code,
          );
          return {
            id: feature?.id ?? code,
            code,
            name: feature?.name ?? code.replaceAll('_', ' '),
            valid:
              feature?.active === true &&
              feature.value_type === 'BOOLEAN' &&
              feature.category === (code === 'multi_tenant' ? 'PLAN_FEATURE' : 'CORE_UNIVERSAL'),
          };
        },
      )
    : [];
  const protectedDiscrepancies = isCorePabx
    ? activePlans.flatMap((plan) =>
        protectedCore.filter(
          (feature) => !feature.valid || protectedCorePabxState(plan, feature.code).discrepancy,
        ),
      )
    : [];
  const premium = activeFeatures.filter((feature) => feature.category === 'PLAN_FEATURE');
  const capacity = activeFeatures.filter((feature) => feature.category === 'CAPACITY');
  const changes = matrixChanges(activePlans, activeFeatures, draft);
  const setEntitlement = (planCode: string, featureCode: string, next: EntitlementValue) => {
    setSuccess('');
    setSaveError('');
    setDraft((current) => ({
      ...current,
      [planCode]: { ...current[planCode], [featureCode]: next },
    }));
  };
  const save = async () => {
    if (!changes.length) return;
    setSaving(true);
    setSaveError('');
    try {
      const submitted: Record<string, Record<string, EntitlementValue>> = {};
      for (const change of changes) {
        submitted[change.planCode] ??= {};
        submitted[change.planCode]![change.featureCode] = change.after;
      }
      await put(`/api/admin/v1/plan-matrix?product=${selectedProduct}`, { plans: submitted });
      setConfirming(false);
      setSuccess('Plan configuration updated successfully.');
      reloadPlans();
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : 'Could not update plan configuration.');
    } finally {
      setSaving(false);
    }
  };
  const booleanControl = (plan: PlanRecord, feature: FeatureRecord) => {
    const enabled = Boolean(draft[plan.code]?.[feature.code]);
    return (
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={`${displayPlanCode(plan.code)}: ${feature.name} ${enabled ? 'ON' : 'OFF'}`}
        className={`entitlement-toggle ${enabled ? 'on' : 'off'}`}
        disabled={readOnly}
        onClick={() => setEntitlement(plan.code, feature.code, !enabled)}
      >
        <span className="toggle-track" aria-hidden="true">
          <span />
        </span>
        <strong>{enabled ? 'ON' : 'OFF'}</strong>
      </button>
    );
  };
  const capacityControl = (plan: PlanRecord, feature: FeatureRecord) => {
    const current = Number(draft[plan.code]?.[feature.code] ?? 1);
    return (
      <div className="capacity-control" aria-label={`${displayPlanCode(plan.code)} ${feature.name}`}>
        <button
          type="button"
          aria-label={`Decrease ${displayPlanCode(plan.code)} ${feature.name}`}
          disabled={readOnly || current <= 1}
          onClick={() => setEntitlement(plan.code, feature.code, Math.max(1, current - 1))}
        >
          −
        </button>
        <input
          aria-label={`${displayPlanCode(plan.code)} ${feature.name} value`}
          type="number"
          min="1"
          step="1"
          disabled={readOnly}
          value={current}
          onChange={(event) =>
            setEntitlement(plan.code, feature.code, Math.max(1, Number(event.target.value) || 1))
          }
        />
        <button
          type="button"
          aria-label={`Increase ${displayPlanCode(plan.code)} ${feature.name}`}
          disabled={readOnly}
          onClick={() => setEntitlement(plan.code, feature.code, current + 1)}
        >
          +
        </button>
      </div>
    );
  };
  const planHeader = (plan: PlanRecord) => (
    <div className="plan-heading">
      <span>{displayPlanCode(plan.code)}</span>
      <strong>
        {isCorePabx
          ? 'CorePABX'
          : plan.code === 'STANDARD'
            ? 'Core'
            : plan.code === 'PROFESSIONAL'
              ? 'Advanced'
              : 'Complete'}
      </strong>
      <small>
        {isCorePabx
          ? `${plan.plan_default_limits?.max_extensions === -1 ? 'Unlimited' : (plan.plan_default_limits?.max_extensions ?? '—')} extensions · ${plan.plan_default_limits?.max_concurrent_calls === -1 ? 'Unlimited' : (plan.plan_default_limits?.max_concurrent_calls ?? '—')} calls · ${plan.plan_default_limits?.max_tenants ?? '—'} tenants default`
          : planCapacityLabel(draft[plan.code]?.multi_pbx)}
      </small>
      {plan.code === 'ENTERPRISE' && <em>Full Feature Set</em>}
    </div>
  );
  return (
    <>
      <PageHeader
        title="Plan Configuration"
        subtitle={readOnly ? 'Read-Only Access' : 'Changes Apply to Newly Issued Leases'}
      />
      <label className="field">
        <span>Product</span>
        <select
          value={selectedProduct}
          onChange={(event) => {
            setSelectedProduct(event.target.value as 'CDRMETRICS' | 'corepabx');
            setConfirming(false);
          }}
        >
          <option value="CDRMETRICS">CDRMetrics</option>
          <option value="corepabx">CorePABX</option>
        </select>
      </label>
      {(planError || featureError) && <div className="error">{planError || featureError}</div>}
      {success && (
        <div className="success" role="status">
          {success}
        </div>
      )}
      {saveError && (
        <div className="error" role="alert">
          {saveError}
        </div>
      )}
      {protectedDiscrepancies.length > 0 && (
        <div className="error" role="alert">
          CorePABX protected entitlement discrepancy detected. Review the affected values below and
          run the policy preflight before a controlled repair. Signed leases may fail until
          resolved.
        </div>
      )}
      <div className="plan-matrix-wrap desktop-matrix">
        <table className="plan-matrix">
          <thead>
            <tr>
              <th>Feature</th>
              {activePlans.map((plan) => (
                <th key={plan.id}>{planHeader(plan)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="matrix-section">
              <th colSpan={activePlans.length + 1}>Core Features</th>
            </tr>
            {!isCorePabx &&
              coreComparisonFeatures.map((name) => (
                <tr key={name}>
                  <th scope="row">{name}</th>
                  {activePlans.map((plan) => (
                    <td key={plan.id}>
                      <button
                        className="core-toggle"
                        disabled
                        aria-label={`${displayPlanCode(plan.code)}: ${name} ON. Core CDRMetrics feature included with every active plan.`}
                        title="Core CDRMetrics feature — included with every active plan."
                      >
                        🔒 <strong>ON</strong>
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            {isCorePabx &&
              protectedCore.map((feature) => (
                <tr key={feature.id}>
                  <th scope="row">
                    {feature.name} <small>Protected</small>
                  </th>
                  {activePlans.map((plan) => {
                    const state = protectedCorePabxState(plan, feature.code);
                    const discrepancy = state.discrepancy || !feature.valid;
                    return (
                      <td key={plan.id}>
                        <button
                          className="core-toggle"
                          disabled
                          aria-label={`${displayPlanCode(plan.code)}: ${feature.name} ${state.observed}${discrepancy ? ', policy discrepancy' : ', protected'}`}
                        >
                          🔒 <strong>{state.observed}</strong>
                        </button>
                        {discrepancy && <span className="warning">Policy discrepancy</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            <tr className="matrix-section">
              <th colSpan={activePlans.length + 1}>Premium Features</th>
            </tr>
            {premium
              .filter((feature) => feature.code !== 'multi_tenant')
              .map((feature) => (
                <tr key={feature.id}>
                  <th scope="row">
                    {feature.name}
                    {feature.coming_soon && <span className="coming-soon">Coming Soon</span>}
                    {isCorePabx && !feature.coming_soon && <small>Implemented</small>}
                  </th>
                  {activePlans.map((plan) => (
                    <td key={plan.id}>{booleanControl(plan, feature)}</td>
                  ))}
                </tr>
              ))}
            <tr className="matrix-section">
              <th colSpan={activePlans.length + 1}>Capacity</th>
            </tr>
            {capacity.map((feature) => (
              <tr key={feature.id}>
                <th scope="row">
                  {feature.name}
                  <small>{feature.description}</small>
                </th>
                {activePlans.map((plan) => (
                  <td key={plan.id}>{capacityControl(plan, feature)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mobile-plan-cards">
        {activePlans.map((plan) => (
          <section className="plan-card" key={plan.id}>
            {planHeader(plan)}
            <p>{plan.description}</p>
            <h3>Core Features</h3>
            {!isCorePabx &&
              coreComparisonFeatures.map((name) => (
                <div className="plan-card-row" key={name}>
                  <span>{name}</span>
                  <span aria-label="Locked and on">🔒 ON</span>
                </div>
              ))}
            {isCorePabx &&
              protectedCore.map((feature) => {
                const state = protectedCorePabxState(plan, feature.code);
                const discrepancy = state.discrepancy || !feature.valid;
                return (
                  <div className="plan-card-row" key={feature.id}>
                    <span>{feature.name} · Protected</span>
                    <span>
                      🔒 {state.observed}
                      {discrepancy && ' · Policy discrepancy'}
                    </span>
                  </div>
                );
              })}
            <h3>Premium Features</h3>
            {premium
              .filter((feature) => feature.code !== 'multi_tenant')
              .map((feature) => (
                <div className="plan-card-row" key={feature.id}>
                  <span>
                    {feature.name}
                    {feature.coming_soon && <span className="coming-soon">Coming Soon</span>}
                    {isCorePabx && !feature.coming_soon && <small>Implemented</small>}
                  </span>
                  {booleanControl(plan, feature)}
                </div>
              ))}
            <h3>Capacity</h3>
            {capacity.map((feature) => (
              <div className="plan-card-row" key={feature.id}>
                <span>{feature.name}</span>
                {capacityControl(plan, feature)}
              </div>
            ))}
          </section>
        ))}
      </div>
      {changes.length > 0 && !readOnly && (
        <div className="unsaved-bar" role="status">
          <strong>
            You have {changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}.
          </strong>
          <div>
            <button className="secondary" onClick={resetDraft}>
              Discard Changes
            </button>
            <button className="primary" onClick={() => setConfirming(true)}>
              Save Plan Changes
            </button>
          </div>
        </div>
      )}
      {confirming && (
        <Modal title="Confirm Plan Changes" close={() => !saving && setConfirming(false)}>
          <div className="change-summary">
            {activePlans.map((plan) => {
              const planChanges = changes.filter((change) => change.planCode === plan.code);
              if (!planChanges.length) return null;
              return (
                <section key={plan.id}>
                  <h3>{displayPlanCode(plan.code)}</h3>
                  {planChanges.map((change) => (
                    <p key={change.featureCode}>
                      <strong>{change.featureName}:</strong> {displayEntitlement(change.before)} →{' '}
                      {displayEntitlement(change.after)}
                    </p>
                  ))}
                </section>
              );
            })}
          </div>
          <p className="muted">
            These changes will apply when affected installations receive their next signed licence
            lease. Existing customer data will not be deleted by plan changes.
          </p>
          {changes.some((change) => change.before === true && change.after === false) && (
            <p className="warning">
              Removing an entitlement may cause existing customers on the affected plan to lose
              access after their next licence refresh.
            </p>
          )}
          {saveError && (
            <div className="error" role="alert">
              {saveError}
            </div>
          )}
          <div className="actions">
            <button
              type="button"
              className="secondary"
              disabled={saving}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
            <button className="primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Confirm & Save'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Customers({ readOnly }: { readOnly: boolean }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Json | null>(null);
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [operationError, setOperationError] = useState('');
  const search = useDebouncedValue(query);
  const endpoint = listEndpoint('/api/admin/v1/customers', search, { status });
  const { data, error, reload } = useData<Json[]>(endpoint);
  const rows = data ?? [];
  const exportColumns: ExportColumn<Json>[] = [
    { label: 'Customer', getValue: (row) => row.name },
    { label: 'Company Name', getValue: (row) => row.company_name },
    { label: 'Primary Email', getValue: (row) => row.primary_email },
    { label: 'Billing Email', getValue: (row) => row.billing_email },
    { label: 'Subscriptions', getValue: (row) => row.subscriptions },
    { label: 'Active Instances', getValue: (row) => row.instances },
    { label: 'Status', getValue: (row) => row.status },
  ];
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await post('/api/admin/v1/customers', {
      name: form.get('name'),
      company_name: form.get('company_name') || null,
      primary_email: form.get('primary_email') || null,
      billing_email: form.get('billing_email') || null,
    });
    setOpen(false);
    reload();
  };
  const updateStatus = async (nextStatus: 'ACTIVE' | 'INACTIVE') => {
    if (!selected) return;
    try {
      setOperationError('');
      await patch(`/api/admin/v1/customers/${String(selected.id)}`, { status: nextStatus });
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Action Failed.');
    }
  };
  const updateCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    const form = new FormData(event.currentTarget);
    try {
      setOperationError('');
      await patch(`/api/admin/v1/customers/${String(selected.id)}`, {
        name: form.get('name'),
        company_name: form.get('company_name') || null,
        primary_email: form.get('primary_email') || null,
        billing_email: form.get('billing_email') || null,
      });
      setEditing(false);
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Update Failed.');
    }
  };
  const deleteCustomer = async () => {
    if (!selected) return;
    const deletionReason = confirmedDeletion(
      'this inactive customer and all associated subscriptions, licenses, and instances',
    );
    if (!deletionReason) return;
    try {
      setOperationError('');
      await remove(`/api/admin/v1/customers/${String(selected.id)}`, {
        reason: deletionReason,
        confirmation: 'DELETE',
      });
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Deletion Failed.');
    }
  };
  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Customer Accounts and Connected Licensing Records"
        action={
          !readOnly && (
            <button className="primary" onClick={() => setOpen(true)}>
              + New Customer
            </button>
          )
        }
      />
      {error && <div className="error">{error}</div>}
      {operationError && <div className="error">{operationError}</div>}
      <div className="table-card">
        <ListToolbar
          query={query}
          setQuery={setQuery}
          placeholder="Search Names, Companies, Emails, or References…"
          title="Customers"
          columns={exportColumns}
          rows={rows}
        >
          <label>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </label>
        </ListToolbar>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Primary Email</th>
              <th>Subscriptions</th>
              <th>Instances</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.id)}>
                <td>
                  <strong>{value(row.name)}</strong>
                  <small>{value(row.company_name)}</small>
                </td>
                <td>{value(row.primary_email)}</td>
                <td>{value(row.subscriptions)}</td>
                <td>{value(row.instances)}</td>
                <td>
                  <Status value={row.status} />
                </td>
                <td>
                  {!readOnly && (
                    <button className="text-button" onClick={() => setSelected(row)}>
                      Actions
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="empty">No Customers Match The Current Filters.</div>}
      </div>
      {selected && !editing && (
        <Modal title={`Manage ${String(selected.name)}`} close={() => setSelected(null)}>
          <div className="action-stack">
            <p>
              Current Status: <Status value={selected.status} />
            </p>
            <button onClick={() => setEditing(true)}>Edit Customer Details</button>
            {selected.status === 'ACTIVE' ? (
              <button onClick={() => void updateStatus('INACTIVE')}>Deactivate Customer</button>
            ) : (
              <>
                <button onClick={() => void updateStatus('ACTIVE')}>Reactivate Customer</button>
                <button className="danger" onClick={() => void deleteCustomer()}>
                  Delete Customer and Associated Records
                </button>
              </>
            )}
          </div>
        </Modal>
      )}
      {selected && editing && (
        <Modal
          title={`Edit ${String(selected.name)}`}
          close={() => {
            setEditing(false);
            setSelected(null);
          }}
        >
          <form onSubmit={(event) => void updateCustomer(event)}>
            <label className="field">
              <span>Customer / Trading Name *</span>
              <input
                name="name"
                defaultValue={typeof selected.name === 'string' ? selected.name : ''}
                required
              />
            </label>
            <label className="field">
              <span>Registered Company Name</span>
              <input
                name="company_name"
                defaultValue={
                  typeof selected.company_name === 'string' ? selected.company_name : ''
                }
              />
            </label>
            <label className="field">
              <span>Primary Email</span>
              <input
                name="primary_email"
                type="email"
                defaultValue={
                  typeof selected.primary_email === 'string' ? selected.primary_email : ''
                }
              />
            </label>
            <label className="field">
              <span>Billing Email</span>
              <input
                name="billing_email"
                type="email"
                defaultValue={
                  typeof selected.billing_email === 'string' ? selected.billing_email : ''
                }
              />
            </label>
            {operationError && <div className="error">{operationError}</div>}
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="primary">Save Customer Details</button>
            </div>
          </form>
        </Modal>
      )}
      {open && (
        <Modal title="Create Customer" close={() => setOpen(false)}>
          <form onSubmit={(e) => void create(e)}>
            <label className="field">
              <span>Customer / Trading Name *</span>
              <input name="name" required />
            </label>
            <label className="field">
              <span>Registered Company Name</span>
              <input name="company_name" />
            </label>
            <label className="field">
              <span>Primary Email</span>
              <input name="primary_email" type="email" />
            </label>
            <label className="field">
              <span>Billing Email</span>
              <input name="billing_email" type="email" />
            </label>
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button className="primary">Create Customer</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function Subscriptions({ readOnly }: { readOnly: boolean }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [billingCycle, setBillingCycle] = useState('');
  const search = useDebouncedValue(query);
  const endpoint = listEndpoint('/api/admin/v1/subscriptions', search, {
    status,
    billing_cycle: billingCycle,
  });
  const { data, error, reload } = useData<Json[]>(endpoint);
  const { data: customers } = useData<Json[]>('/api/admin/v1/customers?limit=1000&status=ACTIVE');
  const { data: plans } = useData<Json[]>('/api/admin/v1/plans');
  const [newProductId, setNewProductId] = useState('');
  const [licenseEnvironment, setLicenseEnvironment] = useState('production');
  const [selectedNewPlanId, setSelectedNewPlanId] = useState('');
  const [selected, setSelected] = useState<Json | null>(null);
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState('');
  const [operationError, setOperationError] = useState('');
  const rows = data ?? [];
  const productChoices = Array.from(
    new Map(
      (plans ?? [])
        .filter((plan) => plan.active)
        .map((plan) => [
          String(plan.product_id),
          { id: String(plan.product_id), name: String(plan.product_name) },
        ]),
    ).values(),
  );
  const selectedNewProductId = newProductId || productChoices[0]?.id || '';
  const newPlanChoices = productPlanChoices((plans ?? []) as PlanChoice[], selectedNewProductId);
  const newPlan = newPlanChoices.find((plan) => plan.id === selectedNewPlanId) ?? newPlanChoices[0];
  const newIsFree = newPlan?.code === 'free';
  const replacementPlans = selected
    ? replacementPlanChoices((plans ?? []) as PlanChoice[], selected.product_id, selected.plan_id)
    : [];
  const pbxLimits = subscriptionPbxLimits(selected ?? {});
  const canEditPbxLimit = canEditSubscriptionPbxLimit(selected?.product_code, selected?.plan_code);
  const exportColumns: ExportColumn<Json>[] = [
    { label: 'Customer', getValue: (row) => row.customer_name },
    { label: 'Product', getValue: (row) => row.product_name ?? row.product_code },
    { label: 'Plan', getValue: (row) => displayPlanCode(row.plan_code) },
    { label: 'Billing Cycle', getValue: (row) => row.billing_cycle },
    { label: 'Period End', getValue: (row) => row.current_period_end },
    { label: 'Active Instances', getValue: (row) => row.active_instances },
    { label: 'Maximum Instances', getValue: (row) => row.max_instances },
    {
      label: 'CDRMetrics PBX Connections',
      getValue: (row) => (row.product_code === 'CDRMETRICS' ? row.effective_multi_pbx : null),
    },
    { label: 'Status', getValue: (row) => row.status },
  ];
  const action = async (name: string, body: unknown = {}) => {
    try {
      setOperationError('');
      await post(`/api/admin/v1/subscriptions/${String(selected?.id)}/${name}`, body);
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Action failed.');
    }
  };
  const deleteSubscription = async () => {
    if (!selected) return;
    const deletionReason = confirmedDeletion(
      'this cancelled subscription and its licenses and instances',
    );
    if (!deletionReason) return;
    try {
      setOperationError('');
      await remove(`/api/admin/v1/subscriptions/${String(selected.id)}`, {
        reason: deletionReason,
        confirmation: 'DELETE',
      });
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Deletion Failed.');
    }
  };
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const plan = plans?.find((item) => item.id === form.get('plan_id'));
    const startDate = form.get('starts_at');
    if (!plan || typeof startDate !== 'string') return;
    await post('/api/admin/v1/subscriptions', {
      customer_id: form.get('customer_id'),
      product_id: plan.product_id,
      plan_id: plan.id,
      billing_cycle: form.get('billing_cycle'),
      type: newIsFree ? 'FREE' : form.get('type'),
      starts_at: `${startDate}T00:00:00.000Z`,
      max_instances: Number(form.get('max_instances')),
    });
    setCreating(false);
    reload();
  };
  return (
    <>
      <PageHeader
        title="Subscriptions"
        subtitle="Explicit Lifecycle Actions; Status Cannot Be Edited Directly"
        action={
          !readOnly && (
            <button className="primary" onClick={() => setCreating(true)}>
              + New Subscription
            </button>
          )
        }
      />
      {error && <div className="error">{error}</div>}
      {operationError && <div className="error">{operationError}</div>}
      <div className="table-card">
        <ListToolbar
          query={query}
          setQuery={setQuery}
          placeholder="Search Customers, Plans, Products, or Subscription IDs…"
          title="Subscriptions"
          columns={exportColumns}
          rows={rows}
        >
          <label>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All Statuses</option>
              {['TRIAL', 'ACTIVE', 'PAST_DUE', 'GRACE', 'SUSPENDED', 'CANCELLED', 'EXPIRED'].map(
                (item) => (
                  <option key={item} value={item}>
                    {titleCaseLabel(item)}
                  </option>
                ),
              )}
            </select>
          </label>
          <label>
            <span>Billing Cycle</span>
            <select value={billingCycle} onChange={(event) => setBillingCycle(event.target.value)}>
              <option value="">All Billing Cycles</option>
              <option value="MONTHLY">Monthly</option>
              <option value="ANNUAL">Annual</option>
            </select>
          </label>
        </ListToolbar>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Product</th>
              <th>Plan</th>
              <th>Cycle</th>
              <th>Period End</th>
              <th>Active / Maximum Instances</th>
              <th>CDRMetrics PBX Connections</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.id)}>
                <td>{value(row.customer_name)}</td>
                <td>{value(row.product_name ?? row.product_code)}</td>
                <td>{displayPlanCode(row.plan_code)}</td>
                <td>{value(row.billing_cycle)}</td>
                <td>{value(row.current_period_end)}</td>
                <td>
                  {value(row.active_instances)} / {value(row.max_instances)}
                </td>
                <td>{row.product_code === 'CDRMETRICS' ? value(row.effective_multi_pbx) : '—'}</td>
                <td>
                  <Status value={row.status} />
                </td>
                <td>
                  {!readOnly && (
                    <button className="text-button" onClick={() => setSelected(row)}>
                      Actions
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="empty">No Subscriptions Match The Current Filters.</div>
        )}
      </div>
      {selected && (
        <Modal title={`Manage ${String(selected.customer_name)}`} close={() => setSelected(null)}>
          <div className="action-stack">
            <p>
              Current State: <Status value={selected.status} />
            </p>
            {selected.product_code === 'CDRMETRICS' && (
              <section className="entitlement-override">
                <strong>Maximum PBX Connections</strong>
                <p>PBX Connections Included: {pbxLimits.planDefault ?? 'Unavailable'}</p>
                <p>Custom Override: {pbxLimits.customOverride ?? 'None'}</p>
                <p>Effective Limit: {pbxLimits.effective ?? 'Unavailable'}</p>
                {canEditPbxLimit ? (
                  <>
                    <form
                      className="embedded-action"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const limit = Number(new FormData(event.currentTarget).get('multi_pbx'));
                        if (!Number.isSafeInteger(limit) || limit < 1) return;
                        void put(
                          `/api/admin/v1/subscriptions/${String(selected.id)}/entitlement-overrides/multi_pbx`,
                          { value: limit },
                        )
                          .then(() => {
                            setSelected(null);
                            reload();
                          })
                          .catch((cause: unknown) =>
                            setOperationError(
                              cause instanceof Error ? cause.message : 'PBX limit update failed.',
                            ),
                          );
                      }}
                    >
                      <label className="field">
                        <span>Custom PBX Connection Limit</span>
                        <input
                          name="multi_pbx"
                          type="number"
                          min="1"
                          step="1"
                          defaultValue={pbxLimits.customOverride ?? pbxLimits.planDefault ?? 1}
                          required
                        />
                      </label>
                      <button type="submit">Save PBX Connection Override</button>
                    </form>
                    <button
                      type="button"
                      disabled={pbxLimits.customOverride === null}
                      onClick={() =>
                        void remove(
                          `/api/admin/v1/subscriptions/${String(selected.id)}/entitlement-overrides/multi_pbx`,
                          {},
                        )
                          .then(() => {
                            setSelected(null);
                            reload();
                          })
                          .catch((cause: unknown) =>
                            setOperationError(
                              cause instanceof Error
                                ? cause.message
                                : 'PBX override removal failed.',
                            ),
                          )
                      }
                    >
                      Remove Override and Use Plan Default
                    </button>
                  </>
                ) : (
                  <p className="field-note">
                    This plan has a fixed PBX connection limit. Upgrade to Enterprise for a custom
                    allowance.
                  </p>
                )}
              </section>
            )}
            {!(selected.product_code === 'corepabx' && selected.plan_code === 'free') && (
              <button onClick={() => void action('renew')}>Renew from Current Period End</button>
            )}
            <button
              onClick={() => {
                const limit = Number(
                  prompt('New maximum instance allowance', String(selected.max_instances)),
                );
                if (Number.isInteger(limit) && limit > 0)
                  void action('change-instance-limit', { max_instances: limit });
              }}
            >
              Change Maximum Instances
            </button>
            {selected.product_code === 'corepabx' && (
              <label className="field">
                <span>New License Environment</span>
                <select
                  value={licenseEnvironment}
                  onChange={(event) => setLicenseEnvironment(event.target.value)}
                >
                  <option value="production">Production</option>
                  <option value="development">Development</option>
                  <option value="staging">Staging</option>
                </select>
              </label>
            )}
            <button
              onClick={() =>
                void post<{ license_key: string }>(
                  `/api/admin/v1/subscriptions/${String(selected.id)}/licenses`,
                  {
                    environment:
                      selected.product_code === 'corepabx' ? licenseEnvironment : 'production',
                  },
                )
                  .then((result) => {
                    setSelected(null);
                    setSecret(result.license_key);
                    reload();
                  })
                  .catch((cause: unknown) =>
                    setOperationError(
                      cause instanceof Error ? cause.message : 'License generation failed.',
                    ),
                  )
              }
            >
              Generate Activation License
            </button>
            <button onClick={() => void action('reactivate')}>Reactivate Subscription</button>
            <button
              onClick={() => {
                const r = confirmedReason('Reason for suspension', 'SUSPEND');
                if (r) void action('suspend', { reason: r, confirmation: 'SUSPEND' });
              }}
              className="danger"
            >
              Suspend Subscription
            </button>
            <button
              onClick={() => {
                const r = confirmedReason('Reason for immediate cancellation', 'CANCEL');
                if (r)
                  void action('cancel', { when: 'IMMEDIATE', reason: r, confirmation: 'CANCEL' });
              }}
              className="danger"
            >
              Cancel Immediately
            </button>
            <button onClick={() => void action('cancel', { when: 'PERIOD_END' })}>
              Cancel at Period End
            </button>
            <form
              className="embedded-action"
              onSubmit={(event) => {
                event.preventDefault();
                const planId = new FormData(event.currentTarget).get('plan_id');
                if (typeof planId === 'string' && planId)
                  void action('change-plan', { plan_id: planId });
              }}
            >
              <label className="field">
                <span>Change Plan</span>
                <select name="plan_id" defaultValue="" required>
                  <option value="" disabled>
                    {replacementPlans.length
                      ? 'Select an active replacement plan…'
                      : 'No other active plans available'}
                  </option>
                  {replacementPlans.map((plan) => (
                    <option key={String(plan.id)} value={String(plan.id)}>
                      {displayPlanCode(plan.code)}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit" disabled={replacementPlans.length === 0}>
                Apply Selected Plan
              </button>
            </form>
            <button
              onClick={() => {
                const cycle = prompt('Enter MONTHLY or ANNUAL')?.toUpperCase();
                if (cycle === 'MONTHLY' || cycle === 'ANNUAL')
                  void action('change-billing-cycle', { billing_cycle: cycle });
              }}
            >
              Change Billing Cycle
            </button>
            {selected.status === 'CANCELLED' && (
              <button className="danger" onClick={() => void deleteSubscription()}>
                Delete Cancelled Subscription
              </button>
            )}
          </div>
        </Modal>
      )}
      {creating && (
        <Modal title="Create Subscription" close={() => setCreating(false)}>
          <form onSubmit={(event) => void create(event)}>
            <label className="field">
              <span>Customer</span>
              <select name="customer_id" required>
                {customers?.map((item) => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {value(item.name)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Product</span>
              <select
                value={selectedNewProductId}
                onChange={(event) => {
                  setNewProductId(event.target.value);
                  setSelectedNewPlanId('');
                }}
                required
              >
                {productChoices.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Plan</span>
              <select
                name="plan_id"
                key={selectedNewProductId}
                value={selectedNewPlanId || (typeof newPlan?.id === 'string' ? newPlan.id : '')}
                onChange={(event) => setSelectedNewPlanId(event.target.value)}
                required
              >
                {newPlanChoices.map((item) => (
                  <option key={String(item.id)} value={String(item.id)}>
                    {displayPlanCode(item.code)}
                  </option>
                ))}
              </select>
            </label>
            {newIsFree && (
              <p className="field-note">
                FREE has no payment or trial requirement. The period is informational and renews
                automatically.
              </p>
            )}
            {!newIsFree && (
              <label className="field">
                <span>Subscription Type</span>
                <select name="type">
                  <option value="PAID">Paid</option>
                  <option value="TRIAL">Trial</option>
                </select>
              </label>
            )}
            <label className="field">
              <span>{newIsFree ? 'Informational Period Interval' : 'Billing Cycle'}</span>
              <select name="billing_cycle">
                <option value="MONTHLY">Monthly</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </label>
            <label className="field">
              <span>Start Date (UTC)</span>
              <input
                name="starts_at"
                type="date"
                defaultValue={new Date().toISOString().slice(0, 10)}
                required
              />
            </label>
            <label className="field">
              <span>Maximum Instances</span>
              <input name="max_instances" type="number" min="1" defaultValue="1" required />
            </label>
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="primary">Review & Create</button>
            </div>
          </form>
        </Modal>
      )}
      {secret && <SecretReveal secret={secret} close={() => setSecret('')} />}
    </>
  );
}

function Licenses({ readOnly }: { readOnly: boolean }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [operationError, setOperationError] = useState('');
  const search = useDebouncedValue(query);
  const endpoint = listEndpoint('/api/admin/v1/licenses', search, { status });
  const { data, error, reload } = useData<Json[]>(endpoint);
  const [selected, setSelected] = useState<Json | null>(null);
  const [secret, setSecret] = useState('');
  const coreLimitLabel = (code: 'max_extensions' | 'max_concurrent_calls' | 'max_tenants') => {
    const limit = (selected?.effective_limits as Json | undefined)?.[code];
    return limit === -1 ? 'Unlimited' : value(limit);
  };
  const openLicense = async (id: unknown) => {
    try {
      setSelected(await api<Json>(`/api/admin/v1/licenses/${String(id)}`));
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'License details unavailable.');
    }
  };
  const saveTenantLimit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    const raw = new FormData(event.currentTarget).get('max_tenants');
    const allowance = Number(raw);
    if (!Number.isSafeInteger(allowance) || allowance < 1) {
      setOperationError('Tenant allowance must be a positive whole number.');
      return;
    }
    try {
      await put(`/api/admin/v1/licenses/${String(selected.id)}/tenant-limit`, {
        max_tenants: allowance,
      });
      await openLicense(selected.id);
      reload();
      setOperationError('');
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Tenant allowance update failed.');
    }
  };
  const rows = data ?? [];
  const exportColumns: ExportColumn<Json>[] = [
    { label: 'Customer', getValue: (row) => row.customer_name },
    { label: 'Product', getValue: (row) => row.product_name ?? row.product_code },
    { label: 'Plan', getValue: (row) => displayPlanCode(row.plan_code) },
    { label: 'Key ID', getValue: (row) => row.key_id },
    { label: 'Subscription ID', getValue: (row) => row.subscription_id },
    { label: 'Status', getValue: (row) => row.status },
    { label: 'Issued At', getValue: (row) => row.issued_at },
    { label: 'Revoked At', getValue: (row) => row.revoked_at },
  ];
  const replace = async () => {
    if (!selected) return;
    const result = await post<{ license_key: string }>(
      `/api/admin/v1/licenses/${String(selected.id)}/replace`,
    );
    setSecret(result.license_key);
    setSelected(null);
    reload();
  };
  const revoke = async () => {
    if (!selected) return;
    const why = confirmedReason('Reason for revoking this license', 'REVOKE');
    if (!why) return;
    await post(`/api/admin/v1/licenses/${String(selected.id)}/revoke`, {
      reason: why,
      confirmation: 'REVOKE',
    });
    setSelected(null);
    reload();
  };
  const deleteLicense = async () => {
    if (!selected) return;
    const deletionReason = confirmedDeletion('this revoked license and its instance history');
    if (!deletionReason) return;
    try {
      setOperationError('');
      await remove(`/api/admin/v1/licenses/${String(selected.id)}`, {
        reason: deletionReason,
        confirmation: 'DELETE',
      });
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Deletion Failed.');
    }
  };
  return (
    <>
      <PageHeader title="Licenses" subtitle="Activation Credentials and Their Lifecycle" />
      {error && <div className="error">{error}</div>}
      {operationError && <div className="error">{operationError}</div>}
      <div className="table-card">
        <ListToolbar
          query={query}
          setQuery={setQuery}
          placeholder="Search Customers, Keys, or Subscription IDs…"
          title="Licenses"
          columns={exportColumns}
          rows={rows}
        >
          <label>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="ACTIVE">Active Only</option>
              <option value="">All Statuses</option>
              <option value="REVOKED">Revoked</option>
              <option value="REPLACED">Replaced</option>
            </select>
          </label>
        </ListToolbar>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Product</th>
              <th>Plan</th>
              <th>Key ID</th>
              <th>Subscription</th>
              <th>Status</th>
              <th>Issued</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.id)}>
                <td>{value(row.customer_name)}</td>
                <td>{value(row.product_name ?? row.product_code)}</td>
                <td>{displayPlanCode(row.plan_code)}</td>
                <td>
                  <code>{value(row.key_id)}</code>
                </td>
                <td>{value(row.subscription_id)}</td>
                <td>
                  <Status value={row.status} />
                </td>
                <td>{value(row.issued_at)}</td>
                <td>
                  {(row.status === 'ACTIVE' || row.status === 'REVOKED') && (
                    <button className="text-button" onClick={() => void openLicense(row.id)}>
                      Details
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="empty">No Licenses Match The Current Filters.</div>}
      </div>
      {selected && (
        <Modal title={`License ${String(selected.key_id)}`} close={() => setSelected(null)}>
          <div className="action-stack">
            <p>
              Product: {value(selected.product_name ?? selected.product_code)} · Plan:{' '}
              {displayPlanCode(selected.plan_code)} · Environment: {value(selected.environment)}
            </p>
            {selected.product_code === 'corepabx' && (
              <section className="entitlement-override">
                <h3>CorePABX Capacity</h3>
                <p>
                  Extensions: {coreLimitLabel('max_extensions')} · Simultaneous calls:{' '}
                  {coreLimitLabel('max_concurrent_calls')}
                </p>
                <p>
                  Tenant plan default:{' '}
                  {value((selected.plan_default_limits as Json | undefined)?.max_tenants)}
                </p>
                <p>
                  Configured tenant override: {value(selected.configured_tenant_override ?? 'None')}
                </p>
                <p>Effective tenant limit: {coreLimitLabel('max_tenants')}</p>
                {selected.plan_code === 'enterprise' &&
                selected.status === 'ACTIVE' &&
                !readOnly ? (
                  <>
                    <form
                      className="embedded-action"
                      onSubmit={(event) => void saveTenantLimit(event)}
                    >
                      <label className="field">
                        <span>Custom Tenant Allowance</span>
                        <input
                          name="max_tenants"
                          type="number"
                          min="1"
                          step="1"
                          required
                          defaultValue={Number(selected.configured_tenant_override ?? 5)}
                        />
                      </label>
                      <button type="submit">Save Tenant Allowance</button>
                    </form>
                    <button
                      type="button"
                      disabled={selected.configured_tenant_override == null}
                      onClick={() =>
                        void put(`/api/admin/v1/licenses/${String(selected.id)}/tenant-limit`, {
                          max_tenants: null,
                        })
                          .then(() => openLicense(selected.id))
                          .catch((cause: unknown) =>
                            setOperationError(
                              cause instanceof Error ? cause.message : 'Reset failed.',
                            ),
                          )
                      }
                    >
                      Use Plan Default
                    </button>
                  </>
                ) : (
                  <p className="field-note">Tenant allowance is locked for this edition.</p>
                )}
              </section>
            )}
            {selected.status === 'ACTIVE' && !readOnly ? (
              <>
                <button onClick={() => void replace()}>Replace Activation Key</button>
                <button className="danger" onClick={() => void revoke()}>
                  Revoke License
                </button>
              </>
            ) : selected.status !== 'ACTIVE' && !readOnly ? (
              <button className="danger" onClick={() => void deleteLicense()}>
                Delete Revoked License
              </button>
            ) : null}
          </div>
        </Modal>
      )}
      {secret && <SecretReveal secret={secret} close={() => setSecret('')} />}
    </>
  );
}

function Instances({ readOnly }: { readOnly: boolean }) {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [operationError, setOperationError] = useState('');
  const search = useDebouncedValue(query);
  const endpoint = listEndpoint('/api/admin/v1/instances', search, { status });
  const { data, error, reload } = useData<Json[]>(endpoint);
  const [selected, setSelected] = useState<Json | null>(null);
  const rows = data ?? [];
  const exportColumns: ExportColumn<Json>[] = [
    { label: 'Customer', getValue: (row) => row.customer_name },
    { label: 'Installation', getValue: (row) => row.display_name ?? row.installation_name },
    { label: 'Instance UUID', getValue: (row) => row.instance_uuid },
    { label: 'Version', getValue: (row) => row.app_version },
    { label: 'Last IP', getValue: (row) => row.last_ip },
    { label: 'Last Seen', getValue: (row) => row.last_seen_at },
    { label: 'License Key ID', getValue: (row) => row.key_id },
    { label: 'License Status', getValue: (row) => row.license_status },
    { label: 'Instance Status', getValue: (row) => row.status },
  ];
  const act = async (action: 'deactivate' | 'revoke') => {
    if (!selected) return;
    const reasonText =
      action === 'revoke'
        ? confirmedReason('Reason for revoking this instance', 'REVOKE')
        : 'Normal VPS replacement';
    if (!reasonText) return;
    await post(
      `/api/admin/v1/instances/${String(selected.id)}/${action}`,
      action === 'revoke' ? { reason: reasonText, confirmation: 'REVOKE' } : { reason: reasonText },
    );
    setSelected(null);
    reload();
  };
  const deleteInstance = async () => {
    if (!selected) return;
    const deletionReason = confirmedDeletion('this deactivated instance and its telemetry history');
    if (!deletionReason) return;
    try {
      setOperationError('');
      await remove(`/api/admin/v1/instances/${String(selected.id)}`, {
        reason: deletionReason,
        confirmation: 'DELETE',
      });
      setSelected(null);
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Deletion Failed.');
    }
  };
  return (
    <>
      <PageHeader title="Instances" subtitle="Installation Health and Version Telemetry" />
      {error && <div className="error">{error}</div>}
      {operationError && <div className="error">{operationError}</div>}
      <div className="table-card">
        <ListToolbar
          query={query}
          setQuery={setQuery}
          placeholder="Search Customers, Installations, Keys, UUIDs, or IPs…"
          title="Instances"
          columns={exportColumns}
          rows={rows}
        >
          <label>
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="ACTIVE">Active Only</option>
              <option value="">All Statuses</option>
              <option value="DEACTIVATED">Deactivated</option>
              <option value="REVOKED">Revoked</option>
            </select>
          </label>
        </ListToolbar>
        <table>
          <thead>
            <tr>
              <th>Customer</th>
              <th>Installation</th>
              <th>Version</th>
              <th>Last Seen</th>
              <th>License</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={String(row.id)}>
                <td>{value(row.customer_name)}</td>
                <td>{value(row.display_name ?? row.hostname ?? row.installation_name)}</td>
                <td>{value(row.app_version)}</td>
                <td>{value(row.last_seen_at)}</td>
                <td>
                  <Status value={row.license_status} />
                </td>
                <td>
                  <Status value={row.status} />
                </td>
                <td>
                  {!readOnly && (row.status === 'ACTIVE' || row.status === 'DEACTIVATED') && (
                    <button className="text-button" onClick={() => setSelected(row)}>
                      Actions
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="empty">No Instances Match The Current Filters.</div>}
      </div>
      {selected && (
        <Modal
          title={`Instance ${value(selected.display_name ?? selected.hostname ?? selected.installation_name)}`}
          close={() => setSelected(null)}
        >
          <div className="action-stack">
            {selected.status === 'ACTIVE' ? (
              <>
                <button onClick={() => void act('deactivate')}>
                  Deactivate for VPS Replacement
                </button>
                <button className="danger" onClick={() => void act('revoke')}>
                  Revoke Compromised Instance
                </button>
              </>
            ) : (
              <button className="danger" onClick={() => void deleteInstance()}>
                Delete Deactivated Instance
              </button>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

function SecretReveal({ secret, close }: { secret: string; close: () => void }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <Modal
      title="One-Time Credential"
      close={() => {
        if (confirmed) close();
      }}
    >
      <p className="muted">
        This credential will only be displayed once. Copy it into an approved secure channel now.
      </p>
      <div className="secret-box">
        <code>{secret}</code>
      </div>
      <button className="secondary full" onClick={() => void navigator.clipboard.writeText(secret)}>
        Copy Credential
      </button>
      <label className="check">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />{' '}
        I have securely copied this credential.
      </label>
      <button className="primary full" disabled={!confirmed} onClick={close}>
        Done
      </button>
    </Modal>
  );
}

function FeatureCatalog({ readOnly, openPlans }: { readOnly: boolean; openPlans: () => void }) {
  const { data, error, reload } = useData<FeatureRecord[]>('/api/admin/v1/features');
  const [query, setQuery] = useState('');
  const [valueType, setValueType] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<FeatureRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<FeatureRecord | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [operationError, setOperationError] = useState('');
  const [success, setSuccess] = useState('');
  const [createType, setCreateType] = useState('BOOLEAN');
  const [createCategory, setCreateCategory] = useState('CORE_UNIVERSAL');
  const catalogue = (data ?? []).filter((feature) => feature.product_code === 'CDRMETRICS');
  const rows = filterFeatures(catalogue, query, valueType, category, status);
  const saveFeature = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected) return;
    setOperationError('');
    const form = new FormData(event.currentTarget);
    try {
      await patch(`/api/admin/v1/features/${selected.id}`, {
        name: form.get('name'),
        description: form.get('description'),
        coming_soon: form.get('coming_soon') === 'on',
        display_order: Number(form.get('display_order')),
      });
      setSelected(null);
      setSuccess('Feature catalogue entry updated successfully.');
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Could not update feature.');
    }
  };
  const createFeature = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const rawDefault = form.get('default_value');
    const defaultValue =
      createType === 'BOOLEAN'
        ? rawDefault === 'true'
        : createType === 'INTEGER'
          ? Number(rawDefault)
          : typeof rawDefault === 'string'
            ? rawDefault
            : '';
    setOperationError('');
    try {
      await post('/api/admin/v1/features', {
        product_id: catalogue[0]?.product_id,
        code: form.get('code'),
        name: form.get('name'),
        description: form.get('description'),
        value_type: createType,
        default_value: defaultValue,
        category: createCategory,
        coming_soon: form.get('coming_soon') === 'on',
        display_order: Number(form.get('display_order')),
      });
      setCreating(false);
      setSuccess('Feature created and seeded across all active plans.');
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Could not create feature.');
    }
  };
  const deactivate = async () => {
    if (!deactivating) return;
    setOperationError('');
    try {
      await post(`/api/admin/v1/features/${deactivating.id}/deactivate`, {
        reason: reasonText,
        confirmation,
      });
      setDeactivating(null);
      setReasonText('');
      setConfirmation('');
      setSelected(null);
      setSuccess('Feature deactivated globally. Newly issued leases will omit it.');
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Could not deactivate feature.');
    }
  };
  const reactivate = async (feature: FeatureRecord) => {
    if (!confirm(`Reactivate ${feature.name} across newly issued licences?`)) return;
    setOperationError('');
    try {
      await post(`/api/admin/v1/features/${feature.id}/reactivate`);
      setSelected(null);
      setSuccess('Feature reactivated successfully.');
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Could not reactivate feature.');
    }
  };
  return (
    <>
      <PageHeader
        title="Feature Catalogue"
        subtitle="Technical Licensing Capabilities and Global Catalogue Status"
        action={
          !readOnly && (
            <button
              className="primary"
              onClick={() => {
                setCreating(true);
                setOperationError('');
              }}
            >
              + New Feature
            </button>
          )
        }
      />
      {error && <div className="error">{error}</div>}
      {operationError && !selected && !creating && !deactivating && (
        <div className="error" role="alert">
          {operationError}
        </div>
      )}
      {success && (
        <div className="success" role="status">
          {success}
        </div>
      )}
      <div className="catalog-counts" aria-label="Catalogue counts">
        <span>
          <strong>{catalogue.length}</strong> Total
        </span>
        <span>
          <strong>{catalogue.filter((feature) => feature.active).length}</strong> Active
        </span>
        <span>
          <strong>{catalogue.filter((feature) => !feature.active).length}</strong> Inactive
        </span>
        <span>
          <strong>{rows.length}</strong> Shown
        </span>
      </div>
      <div className="feature-toolbar">
        <label className="list-search">
          <Search size={16} />
          <input
            aria-label="Search features"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name, code, or description…"
          />
        </label>
        <label>
          <span>Type</span>
          <select value={valueType} onChange={(event) => setValueType(event.target.value)}>
            <option value="">All</option>
            <option value="BOOLEAN">Boolean</option>
            <option value="INTEGER">Integer</option>
            <option value="STRING">String</option>
          </select>
        </label>
        <label>
          <span>Category</span>
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">All</option>
            <option value="CORE_UNIVERSAL">Core / Universal</option>
            <option value="PLAN_FEATURE">Plan Feature</option>
            <option value="CAPACITY">Capacity</option>
          </select>
        </label>
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </label>
      </div>
      <div className="feature-list">
        {rows.map((feature) => {
          const included = feature.plan_usage.filter((usage) =>
            typeof usage.value === 'boolean' ? usage.value : Boolean(usage.value),
          );
          return (
            <article className="feature-card" key={feature.id}>
              <div className="feature-card-main">
                <div className="feature-title">
                  <div>
                    <h2>{feature.name}</h2>
                    <code>{feature.code}</code>
                  </div>
                  <Status value={feature.active ? 'ACTIVE' : 'INACTIVE'} />
                </div>
                <p>{feature.description || 'No description provided.'}</p>
                <div className="feature-badges">
                  <span>{titleCaseLabel(feature.value_type)}</span>
                  <span>{titleCaseLabel(feature.category)}</span>
                  {feature.coming_soon && <span className="coming-soon">Coming Soon</span>}
                </div>
              </div>
              <div className="feature-usage">
                <strong>{feature.category === 'CAPACITY' ? 'Current values' : 'Used by'}</strong>
                {feature.category === 'CAPACITY' ? (
                  feature.plan_usage.map((usage) => (
                    <span key={usage.plan_code}>
                      {displayPlanCode(usage.plan_code)} <b>{displayEntitlement(usage.value)}</b>
                    </span>
                  ))
                ) : (
                  <p>
                    {included.map((usage) => displayPlanCode(usage.plan_code)).join(', ') ||
                      'No active plans'}
                  </p>
                )}
                <button className="text-button" onClick={openPlans}>
                  Open Plan Configuration
                </button>
              </div>
              {!readOnly && (
                <button
                  className="secondary"
                  onClick={() => {
                    setSelected(feature);
                    setOperationError('');
                  }}
                >
                  View / Edit
                </button>
              )}
            </article>
          );
        })}
        {rows.length === 0 && <div className="empty">No Features Match The Current Filters.</div>}
      </div>
      {selected && (
        <Modal title={`Edit ${selected.name}`} close={() => setSelected(null)}>
          <form onSubmit={(event) => void saveFeature(event)}>
            <label className="field">
              <span>Friendly Name</span>
              <input name="name" defaultValue={selected.name} required />
            </label>
            <label className="field">
              <span>Internal Code — locked identifier</span>
              <input value={selected.code} readOnly aria-readonly="true" />
            </label>
            <label className="field">
              <span>Value Type — locked identifier</span>
              <input value={selected.value_type} readOnly aria-readonly="true" />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea name="description" rows={4} defaultValue={selected.description ?? ''} />
            </label>
            <label className="field">
              <span>Category — locked behavioural identifier</span>
              <input value={titleCaseLabel(selected.category)} readOnly aria-readonly="true" />
              <small className="field-note">Fixed after feature creation.</small>
            </label>
            <label className="field">
              <span>Display Order</span>
              <input
                name="display_order"
                type="number"
                min="0"
                defaultValue={selected.display_order}
                required
              />
            </label>
            <label className="check">
              <input name="coming_soon" type="checkbox" defaultChecked={selected.coming_soon} />{' '}
              Coming Soon indicator
            </label>
            {operationError && (
              <div className="error" role="alert">
                {operationError}
              </div>
            )}
            <div className="actions">
              {selected.active ? (
                <button
                  type="button"
                  className="danger-button"
                  onClick={() => {
                    setDeactivating(selected);
                    setSelected(null);
                    setOperationError('');
                  }}
                >
                  Deactivate Globally
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => void reactivate(selected)}
                >
                  Reactivate
                </button>
              )}
              <button type="button" className="secondary" onClick={() => setSelected(null)}>
                Cancel
              </button>
              <button className="primary">Save Feature</button>
            </div>
          </form>
        </Modal>
      )}
      {creating && (
        <Modal title="Create Feature" close={() => setCreating(false)}>
          <form onSubmit={(event) => void createFeature(event)}>
            <label className="field">
              <span>Friendly Name</span>
              <input name="name" required />
            </label>
            <label className="field">
              <span>Internal Code</span>
              <input name="code" pattern="[a-z][a-z0-9_]*" required />
            </label>
            <label className="field">
              <span>Description</span>
              <textarea name="description" rows={3} />
            </label>
            <label className="field">
              <span>Value Type</span>
              <select value={createType} onChange={(event) => setCreateType(event.target.value)}>
                <option value="BOOLEAN">Boolean</option>
                <option value="INTEGER">Integer</option>
                <option value="STRING">String</option>
              </select>
            </label>
            <label className="field">
              <span>Category</span>
              <select
                value={createCategory}
                onChange={(event) => setCreateCategory(event.target.value)}
              >
                <option value="CORE_UNIVERSAL">Core / Universal</option>
                <option value="PLAN_FEATURE">Plan Feature</option>
                <option value="CAPACITY">Capacity</option>
              </select>
            </label>
            <label className="field">
              <span>Default Value</span>
              {createType === 'BOOLEAN' ? (
                <select name="default_value" defaultValue="true">
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : (
                <input
                  name="default_value"
                  type={createType === 'INTEGER' ? 'number' : 'text'}
                  min={createType === 'INTEGER' ? 1 : undefined}
                  defaultValue={createType === 'INTEGER' ? '1' : ''}
                  required
                />
              )}
            </label>
            <label className="field">
              <span>Display Order</span>
              <input name="display_order" type="number" min="0" defaultValue="1000" required />
            </label>
            <label className="check">
              <input name="coming_soon" type="checkbox" /> Coming Soon indicator
            </label>
            {operationError && (
              <div className="error" role="alert">
                {operationError}
              </div>
            )}
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="primary">Create Feature</button>
            </div>
          </form>
        </Modal>
      )}
      {deactivating && (
        <Modal title={`Deactivate ${deactivating.name}`} close={() => setDeactivating(null)}>
          <p className="warning">
            This is a global licensing capability switch. The feature may disappear from newly
            issued leases across every plan. Subscriptions, licence keys, installations, and
            customer data will not be deleted.
          </p>
          <label className="field">
            <span>Reason</span>
            <textarea
              value={reasonText}
              onChange={(event) => setReasonText(event.target.value)}
              rows={3}
              required
            />
          </label>
          <label className="field">
            <span>Type DEACTIVATE to confirm</span>
            <input
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
            />
          </label>
          {operationError && (
            <div className="error" role="alert">
              {operationError}
            </div>
          )}
          <div className="actions">
            <button className="secondary" onClick={() => setDeactivating(null)}>
              Cancel
            </button>
            <button
              className="danger-button"
              disabled={reasonText.trim().length < 3 || confirmation !== 'DEACTIVATE'}
              onClick={() => void deactivate()}
            >
              Deactivate Globally
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function Catalog({ readOnly }: { readOnly: boolean }) {
  const [tab, setTab] = useState<'plans' | 'features'>('plans');
  return (
    <>
      <PageHeader
        title="Plans & Features"
        subtitle="Configurable Database-Backed Entitlement Catalogue"
      />
      <div className="tabs">
        <button className={tab === 'plans' ? 'active' : ''} onClick={() => setTab('plans')}>
          Plans
        </button>
        <button className={tab === 'features' ? 'active' : ''} onClick={() => setTab('features')}>
          Features
        </button>
      </div>
      {tab === 'plans' ? (
        <PlanCatalog readOnly={readOnly} />
      ) : (
        <FeatureCatalog readOnly={readOnly} openPlans={() => setTab('plans')} />
      )}
    </>
  );
}

function AdminUsers() {
  const { data, error, reload } = useData<Json[]>('/api/admin/v1/admin-users');
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<Json | null>(null);
  const [secret, setSecret] = useState('');
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await post<{ temporary_password: string }>('/api/admin/v1/admin-users', {
      display_name: form.get('display_name'),
      email: form.get('email'),
      role: form.get('role'),
    });
    setCreating(false);
    setSecret(result.temporary_password);
    reload();
  };
  const disable = async () => {
    if (!selected) return;
    const why = confirmedReason('Reason for disabling this administrator', 'DISABLE');
    if (!why) return;
    await patch(`/api/admin/v1/admin-users/${String(selected.id)}`, {
      status: 'DISABLED',
      reason: why,
      confirmation: 'DISABLE',
    });
    setSelected(null);
    reload();
  };
  const resetMfa = async () => {
    if (!selected) return;
    const why = confirmedReason('Reason for resetting MFA', 'RESET MFA');
    if (!why) return;
    await post(`/api/admin/v1/admin-users/${String(selected.id)}/reset-mfa`, {
      reason: why,
      confirmation: 'RESET MFA',
    });
    setSelected(null);
    reload();
  };
  return (
    <>
      <PageHeader
        title="Administrators"
        subtitle="MFA Is Mandatory for Every Account"
        action={
          <button className="primary" onClick={() => setCreating(true)}>
            + New Administrator
          </button>
        }
      />
      {error && <div className="error">{error}</div>}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>MFA</th>
              <th>Last Login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.map((row) => (
              <tr key={String(row.id)}>
                <td>{value(row.display_name)}</td>
                <td>{value(row.email)}</td>
                <td>
                  <Status value={row.role} />
                </td>
                <td>
                  <Status value={row.status} />
                </td>
                <td>{row.mfa_enabled ? 'Enrolled' : 'Required'}</td>
                <td>{value(row.last_login_at)}</td>
                <td>
                  <button className="text-button" onClick={() => setSelected(row)}>
                    Actions
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <Modal title="Create Administrator" close={() => setCreating(false)}>
          <form onSubmit={(event) => void create(event)}>
            <label className="field">
              <span>Display Name</span>
              <input name="display_name" required />
            </label>
            <label className="field">
              <span>Email</span>
              <input name="email" type="email" required />
            </label>
            <label className="field">
              <span>Role</span>
              <select name="role">
                <option>ADMIN</option>
                <option>READ_ONLY</option>
                <option>SUPERADMIN</option>
              </select>
            </label>
            <div className="actions">
              <button type="button" className="secondary" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button className="primary">Create Administrator</button>
            </div>
          </form>
        </Modal>
      )}
      {selected && (
        <Modal title={`Manage ${value(selected.display_name)}`} close={() => setSelected(null)}>
          <div className="action-stack">
            <button
              onClick={() =>
                void post(`/api/admin/v1/admin-users/${String(selected.id)}/revoke-sessions`).then(
                  () => setSelected(null),
                )
              }
            >
              Revoke All Sessions
            </button>
            <button className="danger" onClick={() => void resetMfa()}>
              Reset MFA
            </button>
            {selected.status === 'ACTIVE' && (
              <button className="danger" onClick={() => void disable()}>
                Disable Administrator
              </button>
            )}
          </div>
        </Modal>
      )}
      {secret && <SecretReveal secret={secret} close={() => setSecret('')} />}
    </>
  );
}

function Administration({ role }: { role: AdminUser['role'] }) {
  const [tab, setTab] = useState<'users' | 'system' | 'mail-relay' | 'signing' | 'recovery'>('system');
  return (
    <>
      <PageHeader title="Administration" subtitle="Operator Accounts and Safe System Diagnostics" />
      <div className="tabs">
        {role === 'SUPERADMIN' && (
          <button onClick={() => setTab('users')} className={tab === 'users' ? 'active' : ''}>
            Admin Users
          </button>
        )}
        <button onClick={() => setTab('system')} className={tab === 'system' ? 'active' : ''}>
          System
        </button>
        <button
          onClick={() => setTab('mail-relay')}
          className={tab === 'mail-relay' ? 'active' : ''}
        >
          Mail Relay
        </button>
        <button onClick={() => setTab('signing')} className={tab === 'signing' ? 'active' : ''}>
          Signing
        </button>
        {role === 'SUPERADMIN' && (
          <button onClick={() => setTab('recovery')} className={tab === 'recovery' ? 'active' : ''}>
            Backup & Restore
          </button>
        )}
      </div>
      {tab === 'users' ? <AdminUsers /> : tab === 'recovery' ? <Recovery /> :
        <Diagnostics endpoint={`/api/admin/v1/${tab}`} />}
    </>
  );
}

interface RecoveryJob {
  id: string;
  state: 'RUNNING' | 'READY' | 'FAILED';
  created_at: string;
  completed_at?: string;
  size_bytes?: number;
  sha256?: string;
  error_code?: string;
}

function Recovery() {
  const { data, error, reload } = useData<{ available: boolean; jobs: RecoveryJob[] }>(
    '/api/admin/v1/recovery',
  );
  const [passphrase, setPassphrase] = useState('');
  const [repeat, setRepeat] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [operationError, setOperationError] = useState('');
  const [requestedId, setRequestedId] = useState('');
  useEffect(() => {
    const timer = setInterval(reload, 5000);
    return () => clearInterval(timer);
  }, [reload]);
  const create = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (passphrase !== repeat) {
      setOperationError('The two backup passwords do not match.');
      return;
    }
    setBusy(true);
    setOperationError('');
    try {
      const job = await post<{ id: string }>('/api/admin/v1/recovery/backups', {
        passphrase, confirmation,
      });
      setRequestedId(job.id);
      setPassphrase('');
      setRepeat('');
      setConfirmation('');
      reload();
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Backup could not be started.');
    } finally {
      setBusy(false);
    }
  };
  const download = async (id: string) => {
    setOperationError('');
    try {
      await downloadRecoveryBundle(id);
    } catch (cause) {
      setOperationError(cause instanceof Error ? cause.message : 'Backup download failed.');
    }
  };
  return <div className="recovery-panel">
    {error && <div className="error">{error}</div>}
    {operationError && <div className="error" role="alert">{operationError}</div>}
    {data === undefined ? <p>Checking backup service…</p> : !data.available ?
      <p>The backup service is not installed on this host. Ask an operator to run the documented setup command.</p> : <>
      <p>Download one encrypted bundle containing the database, signing keys, application secrets, deployment configuration, and queued mail files. The password cannot be recovered; keep it separately from the file.</p>
      <form className="recovery-form" onSubmit={(event) => void create(event)}>
        <label className="field"><span>Backup password (at least 20 characters)</span>
          <input type="password" autoComplete="new-password" minLength={20} maxLength={1024}
            value={passphrase} onChange={(event) => setPassphrase(event.target.value)} required /></label>
        <label className="field"><span>Repeat backup password</span>
          <input type="password" autoComplete="new-password" minLength={20} maxLength={1024}
            value={repeat} onChange={(event) => setRepeat(event.target.value)} required /></label>
        <label className="field"><span>Type BACKUP to confirm</span>
          <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
        <button className="primary" disabled={busy || confirmation !== 'BACKUP'}>
          {busy ? 'Starting backup…' : 'Create encrypted backup'}
        </button>
      </form>
      {requestedId && !data.jobs.some((job) => job.id === requestedId) &&
        <p role="status">Backup request received. The host service will start it shortly.</p>}
      <h2>Recent backups</h2>
      {data.jobs.length === 0 && <p>No backups have been created through this page.</p>}
      {data.jobs.map((job) => <div className="recovery-job" key={job.id}>
        <div><strong>{job.state}</strong> · {value(job.created_at)}
          {job.state === 'FAILED' && <span className="warning"> Backup failed. Ask an operator to check the recovery service journal.</span>}
          {job.state === 'READY' && <small>SHA-256: {job.sha256}</small>}</div>
        {job.state === 'READY' && <button className="secondary" onClick={() => void download(job.id)}>Download encrypted bundle</button>}
      </div>)}
      <h2>Restore on a new server</h2>
      <p>Use a clean installation at the same reviewed version with an empty database. Put the downloaded file and its password in separate root-only files, then run this command on the new server:</p>
      <code className="recovery-command">sudo bash scripts/restore-recovery-bundle.sh /root/license-manager-backup.lmrec /root/license-manager-recovery.password --confirm-new-install</code>
      <p className="field-note">Follow docs/RECOVERY-BUNDLE.md for the full preparation and verification steps. Restoring replaces database records and signing keys, so it runs offline on the new server.</p>
    </>}
  </div>;
}
function Diagnostics({ endpoint }: { endpoint: string }) {
  const { data, error } = useData<Json | Json[]>(endpoint);
  if (error) return <div className="error">{error}</div>;
  const rows: Json[] = Array.isArray(data)
    ? data
    : Object.entries(data ?? {}).map(([key, val]) => ({
        key,
        value: typeof val === 'object' ? JSON.stringify(val) : val,
      }));
  return (
    <div className="diagnostics">
      {rows.map((row, i) => (
        <div key={i}>
          <span>{value(row.key ?? row.kid)}</span>
          <strong>{value(row.value ?? row.status)}</strong>
        </div>
      ))}
    </div>
  );
}
function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = dialog.current?.querySelector<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? dialog.current)?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== 'Tab' || !dialog.current) return;
      const elements = Array.from(
        dialog.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (!elements.length) return;
      const first = elements[0]!;
      const last = elements.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      previous?.focus();
    };
  }, []);
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={dialog}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon" aria-label="Close" onClick={close}>
            <X />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
